/**
 * @file Hook for managing kanban board operations
 * @remarks
 * Provides handlers for board selection, creation, renaming, and deletion.
 * Includes delete confirmation dialog management with state and JSX component.
 */

import React, { useCallback, useState } from "react";

import type { BoardInfo } from "@/features/player-kanban/components/organisms/BoardTabs/BoardTabsContainer";
import DeleteBoardDialog from "@/features/player-kanban/components/molecules/DeleteBoardDialog/DeleteBoardDialogContainer";

/**
 * Props for the useBoardManagement hook
 * @property boardName - Optional display name of the currently active board
 * @property boards - Array of available board information objects
 * @property onUpdateBoardName - Async callback to update the board name on the server
 * @property onBoardSelect - Callback invoked when a different board is selected from the list
 * @property onCreateBoard - Callback to create a new kanban board
 * @property onMoreBoardOptions - Callback to open the board options menu
 * @property onDeleteBoard - Async callback to delete a board by its ID
 * @property onStartEdit - Callback to initiate board name editing mode
 * @property onSaveEdit - Async callback to save board name changes
 * @property onCancelEdit - Callback to cancel board name editing and revert changes
 * @property onEditChange - Callback invoked when the board name input value changes
 * @property isEditing - Boolean flag indicating if board name is currently being edited
 * @property editValue - Current value of the board name input during editing
 * @property pipelineName - Display name of the pipeline used in the delete confirmation dialog
 */
interface UseBoardManagementProps {
  readonly boardName?: string;
  readonly boards?: BoardInfo[];
  readonly onUpdateBoardName?: (newName: string) => Promise<void>;
  readonly onBoardSelect?: (boardId: string) => void;
  readonly onCreateBoard?: () => void;
  readonly onMoreBoardOptions?: () => void;
  readonly onDeleteBoard?: (boardId: string) => Promise<void>;
  readonly onStartEdit: () => void;
  readonly onSaveEdit: () => Promise<void>;
  readonly onCancelEdit: () => void;
  readonly onEditChange: (value: string) => void;
  readonly isEditing: boolean;
  readonly editValue: string;
  readonly pipelineName: string;
}

/**
 * Return type for the useBoardManagement hook
 * @property handleBoardSelect - Handler for selecting a different board
 * @property handleCreateBoard - Handler for creating a new board
 * @property handleMoreBoardOptions - Handler for opening board options menu
 * @property handleRenameBoard - Handler for initiating board rename
 * @property handleDeleteBoard - Handler for opening delete confirmation dialog
 * @property deleteDialogComponent - JSX element for the delete confirmation dialog
 */
interface UseBoardManagementReturn {
  readonly handleBoardSelect: (boardId: string) => void;
  readonly handleCreateBoard: () => void;
  readonly handleMoreBoardOptions: () => void;
  readonly handleRenameBoard: () => void;
  readonly handleDeleteBoard: () => void;
  readonly deleteDialogComponent: React.JSX.Element;
}

/**
 * Hook for managing kanban board operations including creation, selection, renaming, and deletion.
 * @param props - Hook configuration
 * @param props.boards - Array of available board information objects
 * @param props.onBoardSelect - Callback invoked when a different board is selected
 * @param props.onCreateBoard - Callback to create a new kanban board
 * @param props.onMoreBoardOptions - Callback to open the board options menu
 * @param props.onDeleteBoard - Async callback to delete a board by its ID
 * @param props.onStartEdit - Callback to initiate board name editing mode
 * @param props.pipelineName - Display name used in the delete confirmation dialog
 * @returns Handlers and dialog component for board management
 */
export const useBoardManagement = ({
  boards = [],
  onBoardSelect,
  onCreateBoard,
  onMoreBoardOptions,
  onDeleteBoard,
  onStartEdit,
  pipelineName,
}: UseBoardManagementProps): UseBoardManagementReturn => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleBoardSelect = useCallback(
    (boardId: string) => {
      onBoardSelect?.(boardId);
    },
    [onBoardSelect]
  );

  const handleCreateBoard = useCallback(() => {
    onCreateBoard?.();
  }, [onCreateBoard]);

  const handleMoreBoardOptions = useCallback(() => {
    onMoreBoardOptions?.();
  }, [onMoreBoardOptions]);

  const handleRenameBoard = useCallback(() => {
    onStartEdit();
  }, [onStartEdit]);

  const handleDeleteBoard = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!onDeleteBoard) return;

    const activeBoard = boards.find(board => board.isActive);
    if (!activeBoard) return;

    if (boards.length <= 1) {
      console.warn("Cannot delete the last remaining board");
      return;
    }

    try {
      setIsDeleting(true);
      await onDeleteBoard(activeBoard.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Failed to delete board:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [onDeleteBoard, boards]);

  return {
    handleBoardSelect,
    handleCreateBoard,
    handleMoreBoardOptions,
    handleRenameBoard,
    handleDeleteBoard,
    deleteDialogComponent: (
      <DeleteBoardDialog
        isOpen={showDeleteDialog}
        isDeleting={isDeleting}
        boardName={pipelineName}
        onClose={handleCloseDeleteDialog}
        onConfirmDelete={handleConfirmDelete}
      />
    ),
  };
};
